import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { SharedWorkspace, SharedWorkspaceService } from '../services/shared-workspace.service';

export interface ShareDialogData {
  /** MEMORY or RECIPE - only used for wording and the audit line. */
  type: 'MEMORY' | 'RECIPE';
  /** Identifies the item to XDB. */
  queryHash: string;
  /** The workspace the item lives in. Must be one the caller owns. */
  sourceWorkspaceId: number;
  /** The profile that wrote it. */
  userKey: string;
  /** First line of the item, so the dialog can show what is being shared. */
  preview?: string;
}

/**
 * Choose which shared workspaces an item belongs in.
 *
 * The selection is the complete answer, not an addition: XDB's shareMemory
 * takes the whole list of workspace keys, so unticking everything unshares the
 * item. The dialog says so, because "share" and "set the sharing" look
 * identical until the moment somebody unticks a box and loses something.
 */
@Component({
  selector: 'app-share-to-workspace-dialog',
  standalone: false,
  templateUrl: './share-to-workspace-dialog.component.html',
  styleUrls: ['./share-to-workspace-dialog.component.css'],
})
export class ShareToWorkspaceDialogComponent implements OnInit {

  workspaces: SharedWorkspace[] = [];
  selected: { [id: number]: boolean } = {};
  loading = true;
  busy = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ShareDialogData,
    private dialogRef: MatDialogRef<ShareToWorkspaceDialogComponent>,
    private shared: SharedWorkspaceService,
    private toastr: ToastrService,
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      const list = await this.shared.listWorkspaces();
      this.workspaces = (list?.owned || []).filter(w => w.shareable);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.loading = false;
    }
  }

  get chosen(): number[] {
    return this.workspaces
      .filter(w => this.selected[w.workspaceId])
      .map(w => w.workspaceId);
  }

  get label(): string {
    return this.data.type === 'RECIPE' ? 'recipe' : 'memory';
  }

  async save(): Promise<void> {
    this.busy = true;
    try {
      await this.shared.shareItem({
        queryHash: this.data.queryHash,
        sourceWorkspaceId: this.data.sourceWorkspaceId,
        userKey: this.data.userKey,
        workspaceIds: this.chosen,
        type: this.data.type,
        title: (this.data.preview || '').trim().slice(0, 300),
      });
      this.toastr.success(
        this.chosen.length
          ? `Shared into ${this.chosen.length} workspace${this.chosen.length > 1 ? 's' : ''}.`
          : `This ${this.label} is no longer shared.`);
      this.dialogRef.close(true);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
